import {
  HardwareRequest,
  HardwareRequestStatus,
  HardwareSku,
  HardwareSkuType,
  User,
} from "src/api/types.generated";

import {
  THardwareItemCardButton,
  THardwareItemCardProps,
} from "../../components/Hardware/ItemCard";

export const processRequests = ({
  data,
  items,
  userType,
  getButtons,
  onClick,
  hideLottery,
  onQuantityIncrease,
  onQuantityDecrease,
  onRemoveItem,
}: {
  data:
    | (Pick<
        HardwareRequest,
        | "created_at"
        | "updated_at"
        | "status"
        | "id"
        | "user_id"
        | "event_id"
        | "requested_at"
        | "accepted_at"
        | "ready_at"
        | "picked_up_at"
        | "returned_at"
        | "denied_at"
        | "cancelled_at"
      > & {
        user: Pick<User, "name" | "email">;
        hardware_item_requests?: Array<{
          id: number;
          quantity: number;
          hardware_item_id: number;
          HardwareItem: {
            id: number;
            sku_code: string;
            available_quantity: number;
          };
        }>;
      })[]
    | undefined;
  items: Pick<
    HardwareSku,
    | "code"
    | "image"
    | "type"
    | "name"
    | "location"
    | "description"
    | "category_id"
  >[];
  userType: "organizer" | "hacker";
  // getButtons is here since we need to pass different onClick functions retrieved from generated graphql hooks
  getButtons: (
    status: HardwareRequestStatus
  ) => THardwareItemCardButton[] | undefined;
  // item name onclick
  onClick?: (requestId: number, skuCode: string) => void;
  hideLottery?: boolean;
  // quantity modification handlers for organizer
  onQuantityIncrease?: (
    requestId: number,
    hardwareItemId: number,
    currentQuantity: number
  ) => void;
  onQuantityDecrease?: (
    requestId: number,
    hardwareItemId: number,
    currentQuantity: number
  ) => void;
  // remove item handler for organizer
  onRemoveItem?: (requestId: number, hardwareItemId: number) => void;
}) => {
  if (!data) return [];

  // Initialize an object with all HardwareRequestStatus as keys and empty arrays as values
  const requestGroups: Record<HardwareRequestStatus, THardwareItemCardProps[]> =
    Object.values(HardwareRequestStatus).reduce((acc, status) => {
      acc[status] = [];
      return acc;
    }, {} as Record<HardwareRequestStatus, THardwareItemCardProps[]>);

  // Populate the requestGroups object with the appropriate cards
  data.forEach((request) => {
    // Check if request has hardware_item_requests (new structure)
    if (
      "hardware_item_requests" in request &&
      request.hardware_item_requests?.length
    ) {
      // Group all items in this request together
      const requestItems: THardwareItemCardProps[] = [];

      request.hardware_item_requests.forEach((itemRequest) => {
        // Add safety check for HardwareItem
        if (!itemRequest.HardwareItem) {
          return;
        }

        const skuInfo = items.find(
          (item) => item.code === itemRequest.HardwareItem.sku_code
        );

        if (!skuInfo) {
          return;
        }

        const isLottery = skuInfo.type === HardwareSkuType.Lottery;
        if (hideLottery && isLottery) return;

        // Generate buttons - include quantity modification for REQUESTED status when userType is organizer
        let buttons = getButtons(request.status);

        // Add quantity modification buttons for REQUESTED status when userType is organizer
        if (
          userType === "organizer" &&
          request.status === HardwareRequestStatus.Requested &&
          onQuantityIncrease &&
          onQuantityDecrease
        ) {
          const quantityButtons: THardwareItemCardButton[] = [
            {
              text: "-",
              onClick: () =>
                onQuantityDecrease(
                  request.id,
                  itemRequest.HardwareItem.id,
                  itemRequest.quantity
                ),
              color: "secondary",
            },
            {
              text: "+",
              onClick: () =>
                onQuantityIncrease(
                  request.id,
                  itemRequest.HardwareItem.id,
                  itemRequest.quantity
                ),
              color: "secondary",
            },
          ];

          // Add remove button if onRemoveItem is provided
          if (onRemoveItem) {
            quantityButtons.push({
              text: "Remove",
              onClick: () =>
                onRemoveItem(request.id, itemRequest.hardware_item_id),
              color: "danger",
            });
          }

          // Combine existing buttons with quantity buttons
          buttons = buttons
            ? [...buttons, ...quantityButtons]
            : quantityButtons;
        }

        // Data for item cards
        const cardProps: THardwareItemCardProps = {
          skuCode: skuInfo.code,
          requestId: request.id,
          image: skuInfo.image,
          status: undefined, // Remove status from individual items
          label: isLottery
            ? [
                {
                  text: "Special Item",
                  color: "primary-light",
                },
              ]
            : undefined,
          title: skuInfo.name,
          description: skuInfo.location
            ? `Location: ${skuInfo.location}`
            : undefined, // Show location in description
          quantity: itemRequest.quantity,
          createdAt: request.created_at, // Add request creation date for sorting
          submitted_by: undefined, // Don't show submitted_by on individual items
          timestamp: userType === "organizer" ? request.created_at : undefined,
          requested_at: request.requested_at,
          accepted_at: request.accepted_at,
          ready_at: request.ready_at,
          picked_up_at: request.picked_up_at,
          returned_at: request.returned_at,
          denied_at: request.denied_at,
          cancelled_at: request.cancelled_at,
          showAllTimestamps: false, // Don't show timestamps on individual items
          buttons: buttons,
          onClick: onClick
            ? () => onClick(request.id, skuInfo.code)
            : undefined,
          showRemainingQuantity: true, // Show remaining quantity for packing
          remainingQuantity: itemRequest.HardwareItem.available_quantity,
        };
        requestItems.push(cardProps);
      });

      // Add all items from this request as a group
      if (requestItems.length > 0) {
        requestGroups[request.status].push(...requestItems);
      }
    } else {
      // Fallback for requests without items (empty requests)
      const cardProps: THardwareItemCardProps = {
        skuCode: "",
        requestId: request.id,
        image: "",
        status: request.status,
        title: `Hardware Request #${request.id}`,
        description: `Status: ${request.status} - No items`,
        quantity: 0,
        submitted_by: undefined, // Don't show submitted_by on individual items
        timestamp: userType === "organizer" ? request.created_at : undefined,
        requested_at: request.requested_at,
        accepted_at: request.accepted_at,
        ready_at: request.ready_at,
        picked_up_at: request.picked_up_at,
        returned_at: request.returned_at,
        denied_at: request.denied_at,
        cancelled_at: request.cancelled_at,
        showAllTimestamps: false, // Don't show timestamps on individual items
        buttons: getButtons(request.status),
        onClick: onClick ? () => onClick(request.id, "") : undefined,
        remainingQuantity: 0,
      };
      requestGroups[request.status].push(cardProps);
    }
  });

  return (
    Object.entries(requestGroups)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, items]) => items.length > 0)
      .map(([status, items]) => ({
        status: status as HardwareRequestStatus,
        items,
      }))
  );
};
